import { buildVenuePath } from '../utils/venueRouting';
import React, { useState, useContext, useEffect } from 'react';
import AppContext from '../contexts/AppContext';
import {
  FaBars, FaPhone, FaEnvelope, FaGlobe, FaBook, FaRegSave,
  FaWhatsapp, FaFacebook, FaInstagram, FaMapMarkerAlt
} from "react-icons/fa";
import { FaSquareXTwitter, FaWeixin } from "react-icons/fa6";
import { useNavigate } from 'react-router-dom';
import { resolveReadableColor } from '../utils/ColorLuminance.js';
import { contactLog } from '../utils/Accesslog.js';
import { updateVenueManifest } from '../utils/DynamicPWA.js';
import { message } from 'antd';
import NotificationToggle from './NotificationToggle';
import InstallGuide from './InstallGuide';
import { getInstallState, installMode, startInstall, subscribeInstallState } from '../utils/installPrompt';
import NotificationPrompt from './NotificationPrompt';

// wa.me requires a bare international number - digits only, no leading "+",
// no spaces/parens/dashes. Saved contact values aren't guaranteed to
// already be in that shape (legacy data predating stricter validation, or
// anything entered with formatting characters), so this strips everything
// else before building the link rather than trusting the stored value
// as-is - a value like "+61 412 345 678" would otherwise build a wa.me URL
// that fails to open.
function toWhatsAppDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

// The backend URL validator for these fields allows the protocol to be
// omitted (e.g. "facebook.com/xxx" is accepted and saved as-is), but
// window.open() treats a protocol-less string as a path on this site's own
// origin instead of navigating away, silently sending guests to a broken
// in-app route instead of the actual page.
function openExternalUrl(url) {
  if (!url) return;
  const hasProtocol = /^https?:\/\//i.test(url);
  window.open(hasProtocol ? url : `https://${url}`);
}

const TopTabs = () => {
  // Read venue info and theme from context
  const { hotelInfo, venueId, theme } = useContext(AppContext);
  const [showIcons, setShowIcons] = useState(false);
  // Which add-to-Home-Screen help is open (null = none)
  const [installGuide, setInstallGuide] = useState(null);
  // The browser's install offer is kept outside this component (see installPrompt.js), because this component
  // unmounts on detail pages and the browser only makes the offer once per page load.
  const [installState, setInstallState] = useState(getInstallState);
  const navigate = useNavigate();

  // Toggle the contact drawer
  const handleInfoClick = () => {
    setShowIcons(!showIcons);
  };

  // Follow the install offer: it may have arrived before this mounted, or arrive later, or the app may get installed
  useEffect(() => {
    setInstallState(getInstallState());
    return subscribeInstallState(() => setInstallState(getInstallState()));
  }, []);
  const alreadyInstalled = installMode(window, installState, venueId) === 'installed';

  // A tap always ends in something the guest can see: the browser's own install dialog, or help for their device
  const handleAddToHomeScreen = async () => {
    const { guide } = await startInstall(window, venueId);
    if (guide) setInstallGuide(guide);
  };

  // Update the PWA manifest name dynamically
  useEffect(() => {
    if (hotelInfo && hotelInfo.name) {
      updateVenueManifest(venueId, hotelInfo.name, hotelInfo.alias);
    }
  }, [hotelInfo, venueId]);


  if (!hotelInfo) { return <div>Loading...</div>; }
  const themeColor = theme?.primaryColor || theme?.color || theme?.topBannerBackground || 'rgb(0,0,0)';
  const usesPresetThemePalette = Boolean(theme?.color || theme?.primaryColor);
  const defaultDrawerBackground = 'rgb(255,255,255)';
  const defaultDrawerTitleColor = 'rgb(17,24,39)';
  const defaultDrawerDescriptionColor = 'rgb(55,65,81)';
  const contactBackground = usesPresetThemePalette
    ? defaultDrawerBackground
    : (theme?.contactBackground || defaultDrawerBackground);
  // Both branches now go through the same contrast check against the
  // ACTUAL resolved drawer background, instead of only the preset-palette
  // branch checking (and only against an assumed-white background) - two
  // independently admin-picked theme colors (contactNameIcons vs
  // contactBackground) could otherwise land on the same side of light/dark
  // and become unreadable, which is exactly what happened here.
  const designedColor = usesPresetThemePalette
    ? resolveReadableColor(themeColor, contactBackground, defaultDrawerTitleColor)
    : resolveReadableColor(theme?.contactNameIcons, contactBackground, defaultDrawerTitleColor);
  const topIcons = theme?.color ? 'rgb(255,255,255)' : (theme?.secondaryColor ? theme?.secondaryColor : theme?.topIcons || 'rgb(0,0,0)');
  const contactDescriptionsColor = usesPresetThemePalette
    ? defaultDrawerDescriptionColor
    : resolveReadableColor(theme?.contactDescriptions, contactBackground, defaultDrawerDescriptionColor);

  return (
    <div>
      {/* Asks once whether the guest wants this venue's notifications (venues with them switched on only) */}
      <NotificationPrompt venueId={venueId} venueName={hotelInfo && hotelInfo.name} hold={Boolean(installGuide)} />

      {/* Top navigation bar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-20 flex justify-between pl-4 pr-4 shadow-xl"
        style={{ backgroundColor: themeColor }}
      >
        <button onClick={handleInfoClick} className="p-2">
          <FaBars
            className={`h-6 w-6 transform transition-transform duration-500 ease-in-out ${showIcons ? '' : '-rotate-180'}`}
            style={{ color: topIcons }}
          />
        </button>
        <span className="h-full flex items-center justify-center">
          <img
            src={hotelInfo.landing.logoImage}
            onClick={() => navigate(buildVenuePath('/', venueId))}
            alt="logo"
            className="max-h-16"
            style={{ verticalAlign: 'middle' }}
          />
        </span>

        {/* Show Save me only on the home page, and not once it is already on the Home Screen */}
        {window.location.pathname === '/' && !alreadyInstalled ? (
          <button
            onClick={handleAddToHomeScreen}
            aria-label="Save me: add this page to your Home Screen"
            className="px-2 pt-2 flex flex-col items-center justify-center gap-0"
            style={{ color: topIcons }}
          >
            <FaRegSave className="h-6 w-6" />
            <span className="text-[0.6rem] font-semibold">Save me</span>
          </button>
        ) : (
          <div className="p-2 flex flex-col items-center justify-center gap-0 invisible" style={{ color: topIcons }}>
            <FaRegSave className="h-6 w-6" />
            <span className="text-[0.6rem] font-semibold">Save me</span>
          </div>
        )}


      </div>

      {/* Click outside to close the drawer */}
      {showIcons && (
        <div
          className="fixed inset-0 bg-opacity-25 z-20 ml-0 bg-black"
          onClick={() => setShowIcons(false)}
        ></div>
      )}

      {/* Contact drawer content */}
      <div
        style={{ backgroundColor: contactBackground }}
        className={`fixed inset-0  ml-0 w-3/4 mt-20 flex flex-col pl-4 mb-16 ${showIcons ? 'z-40' : 'invisible'
          } overflow-y-auto transition-transform duration-500 ease-in-out ${showIcons ? '-translate-x-0' : '-translate-x-60'
          }`}
      >
        {/* Venue notifications (hidden when this device cannot receive them) */}
        <NotificationToggle
          venueId={venueId}
          venueName={hotelInfo && hotelInfo.name}
          color={designedColor}
          onNeedInstall={handleAddToHomeScreen}
        />

        {/* Navigate to Venue */}
        {hotelInfo && hotelInfo.landing && hotelInfo.landing.geoLocation && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() =>
              window.open(
                `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  hotelInfo.landing.geoLocation.latitude
                )},${encodeURIComponent(hotelInfo.landing.geoLocation.longitude)}`
              )
            }
          >
            <FaMapMarkerAlt className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                Navigate to My Venue
              </p>
            </span>
          </span>
        )}

        {/* Phone */}
        {hotelInfo.contact.phone && hotelInfo.contact.phone.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'Phone');
              window.open(`tel:${hotelInfo.contact.phone.value}`)
            }
            }
          >
            <FaPhone className="w-6 h-6 flex-none rotate-90" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.phone.name || 'Phone'}
              </p>
              <p className="text-sm" style={{ color: contactDescriptionsColor }}>
                {hotelInfo.contact.phone.description}
              </p>
            </span>
          </span>
        )}

        {/* Email */}
        {hotelInfo.contact.email && hotelInfo.contact.email.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"

            onClick={() => {
              contactLog(venueId, 'Email');
              window.open(`mailto:${hotelInfo.contact.email.value}`)
            }
            }
          >
            <FaEnvelope className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.email.name || 'Email'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.email.description}
              </p>
            </span>
          </span>
        )}

        {/* Whatsapp */}
        {hotelInfo.contact.whatsapp && hotelInfo.contact.whatsapp.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'Whatsapp');
              const digits = toWhatsAppDigits(hotelInfo.contact.whatsapp.value);
              if (digits) {
                window.open(`https://wa.me/${digits}`);
              }
            }
            }
          >
            <FaWhatsapp className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.whatsapp.name || 'WhatsApp'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.whatsapp.description}
              </p>
            </span>
          </span>
        )}

        {/* Wechat */}
        {hotelInfo.contact.wechat && hotelInfo.contact.wechat.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={async () => {
              const wechatId = hotelInfo.contact.wechat.value;
              try {
                await navigator.clipboard.writeText(wechatId);
                message.success('WeChat ID copied. Please open WeChat to add the contact.');
              } catch (err) {
                console.error('Copy failed', err);
                message.error('Failed to copy. Please copy the WeChat ID manually.');
              }
            }}
          >
            <FaWeixin className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.wechat.name || 'WeChat'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.wechat.description}
              </p>
            </span>
          </span>
        )}


        {/* Website */}
        {hotelInfo.contact.website && hotelInfo.contact.website.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'Website');
              openExternalUrl(hotelInfo.contact.website.value)
            }
            }
          >
            <FaGlobe className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.website.name || 'Website'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.website.description}
              </p>
            </span>
          </span>
        )}

        {/* E-Book / Printable Version */}
        {hotelInfo.contact.serviceDirectory && hotelInfo.contact.serviceDirectory.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              openExternalUrl(hotelInfo.contact.serviceDirectory.value);
            }}
          >
            <FaBook className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.serviceDirectory.name || 'E-Book / Printable Version'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.serviceDirectory.description}
              </p>
            </span>
          </span>
        )}

        {/* Facebook */}
        {hotelInfo.contact.facebook && hotelInfo.contact.facebook.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'Facebook');
              openExternalUrl(hotelInfo.contact.facebook.value)
            }
            }
          >
            <FaFacebook className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                Facebook
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.facebook.description}
              </p>
            </span>
          </span>
        )}

        {/* Instagram */}
        {hotelInfo.contact.instagram && hotelInfo.contact.instagram.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'Instagram');
              openExternalUrl(hotelInfo.contact.instagram.value)
            }}
          >
            <FaInstagram className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.instagram.name || 'Instagram'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.instagram.description}
              </p>
            </span>
          </span>
        )}

        {/* X (formerly Twitter) */}
        {hotelInfo.contact.X && hotelInfo.contact.X.value && (
          <span
            className="text-lg cursor-pointer pt-3 pb-3 flex items-center gap-4 border-b-2"
            onClick={() => {
              contactLog(venueId, 'X');
              openExternalUrl(hotelInfo.contact.X.value)
            }}
          >
            <FaSquareXTwitter className="w-6 h-6 flex-none" style={{ color: designedColor }} />
            <span className="grow">
              <p style={{ color: designedColor }} className="font-semibold">
                {hotelInfo.contact.X.name || 'X (Twitter)'}
              </p>
              <p style={{ color: contactDescriptionsColor }} className="text-sm">
                {hotelInfo.contact.X.description}
              </p>
            </span>
          </span>
        )}
      </div>

      {/* How to add the page to the Home Screen when the browser cannot do it from a button */}
      {installGuide && <InstallGuide mode={installGuide} onClose={() => setInstallGuide(null)} />}
    </div>
  );
};

export default TopTabs;


